import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  GitCompare, 
  Upload, 
  FileText, 
  Download, 
  Loader2,
  AlertCircle,
  CheckCircle2,
  MinusCircle,
  PlusCircle,
  Eye,
  ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function DocumentComparison({ user, cabinet, onLogout }) {
  const navigate = useNavigate();
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [viewMode, setViewMode] = useState("side-by-side"); // side-by-side, unified, changes-only

  const handleFileUpload = (fileNumber, event) => {
    const file = event.target.files[0];
    if (file) {
      if (fileNumber === 1) {
        setFile1(file);
      } else {
        setFile2(file);
      }
      toast.success(`Document ${fileNumber} chargé`);
    }
  };

  const compareDocuments = async () => {
    if (!file1 || !file2) {
      toast.error("Veuillez charger les deux documents");
      return;
    }

    setComparing(true);
    try {
      const formData = new FormData();
      formData.append("document1", file1);
      formData.append("document2", file2);

      const token = localStorage.getItem('lawliet_token');
      const response = await axios.post(
        `${API}/documents/compare`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          params: { cabinet_id: cabinet._id }
        }
      );

      setComparisonResult(response.data);
      toast.success("Comparaison terminée !");
    } catch (error) {
      console.error("Comparison error:", error);
      toast.error("Erreur lors de la comparaison");
    } finally {
      setComparing(false);
    }
  };

  const downloadReport = () => {
    if (!comparisonResult) return;

    const report = generateReport(comparisonResult);
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparaison_${file1.name}_vs_${file2.name}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Rapport téléchargé !");
  };

  const generateReport = (result) => {
    let report = "═══════════════════════════════════════════════════════\n";
    report += "       RAPPORT DE COMPARAISON DE DOCUMENTS\n";
    report += "═══════════════════════════════════════════════════════\n\n";
    report += `Document 1: ${file1.name}\n`;
    report += `Document 2: ${file2.name}\n`;
    report += `Date: ${new Date().toLocaleDateString('fr-FR')}\n`;
    report += `Similarité: ${result.similarity_score}%\n\n`;
    
    report += "─────────────────────────────────────────────────────\n";
    report += "RÉSUMÉ DES MODIFICATIONS\n";
    report += "─────────────────────────────────────────────────────\n\n";
    report += `✓ Ajouts: ${result.additions?.length || 0}\n`;
    report += `✗ Suppressions: ${result.deletions?.length || 0}\n`;
    report += `≈ Modifications: ${result.modifications?.length || 0}\n\n`;
    
    if (result.critical_changes?.length > 0) {
      report += "─────────────────────────────────────────────────────\n";
      report += "⚠️  MODIFICATIONS CRITIQUES\n";
      report += "─────────────────────────────────────────────────────\n\n";
      result.critical_changes.forEach((change, idx) => {
        report += `${idx + 1}. ${change.type}: ${change.description}\n`;
        report += `   Gravité: ${change.severity}\n`;
        report += `   Impact: ${change.impact}\n\n`;
      });
    }
    
    return report;
  };

  const resetComparison = () => {
    setFile1(null);
    setFile2(null);
    setComparisonResult(null);
    setViewMode("side-by-side");
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900 mb-2 flex items-center gap-3">
            <GitCompare className="h-8 w-8" />
            Comparaison de Documents
          </h1>
          <p className="text-slate-600">
            Comparez deux versions d'un document et identifiez automatiquement les modifications juridiques importantes
          </p>
        </div>

        {!comparisonResult ? (
          <>
            {/* Upload Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Document 1 */}
              <Card className={file1 ? "border-blue-500 border-2" : ""}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Document Original (Version 1)
                  </CardTitle>
                  <CardDescription>
                    Chargez la première version du document
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!file1 ? (
                    <label className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition">
                      <Upload className="h-12 w-12 text-slate-400 mb-3" />
                      <span className="text-sm font-medium text-slate-700">Cliquez pour charger</span>
                      <span className="text-xs text-slate-500 mt-1">PDF, DOCX, TXT (max 10MB)</span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.txt"
                        onChange={(e) => handleFileUpload(1, e)}
                      />
                    </label>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="font-medium text-slate-900">{file1.name}</p>
                            <p className="text-xs text-slate-500">{(file1.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFile1(null)}
                        >
                          Changer
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Document 2 */}
              <Card className={file2 ? "border-orange-500 border-2" : ""}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-orange-600" />
                    Document Modifié (Version 2)
                  </CardTitle>
                  <CardDescription>
                    Chargez la version modifiée du document
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!file2 ? (
                    <label className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-500 transition">
                      <Upload className="h-12 w-12 text-slate-400 mb-3" />
                      <span className="text-sm font-medium text-slate-700">Cliquez pour charger</span>
                      <span className="text-xs text-slate-500 mt-1">PDF, DOCX, TXT (max 10MB)</span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.txt"
                        onChange={(e) => handleFileUpload(2, e)}
                      />
                    </label>
                  ) : (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="font-medium text-slate-900">{file2.name}</p>
                            <p className="text-xs text-slate-500">{(file2.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFile2(null)}
                        >
                          Changer
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Compare Button */}
            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={compareDocuments}
                disabled={!file1 || !file2 || comparing}
                className="bg-blue-600 hover:bg-blue-700 min-w-[300px]"
              >
                {comparing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Comparaison en cours...
                  </>
                ) : (
                  <>
                    <GitCompare className="h-5 w-5 mr-2" />
                    Comparer les Documents
                  </>
                )}
              </Button>
            </div>

            {/* Info Card */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Eye className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-blue-900 mb-2">
                      Analyse IA Intelligente
                    </h3>
                    <ul className="text-sm text-slate-700 space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Détection automatique des modifications juridiques importantes</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Identification des clauses ajoutées, supprimées ou modifiées</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Analyse sémantique (pas juste textuelle)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Score de similarité et rapport téléchargeable</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Comparison Results */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <GitCompare className="h-6 w-6 text-blue-600" />
                    Résultats de la Comparaison
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={downloadReport}>
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                    <Button variant="outline" onClick={resetComparison}>
                      Nouvelle Comparaison
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Similarité</span>
                    </div>
                    <p className="text-3xl font-bold text-green-600">
                      {comparisonResult.similarity_score}%
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <PlusCircle className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Ajouts</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-600">
                      {comparisonResult.additions?.length || 0}
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <MinusCircle className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-medium text-red-900">Suppressions</span>
                    </div>
                    <p className="text-3xl font-bold text-red-600">
                      {comparisonResult.deletions?.length || 0}
                    </p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <ArrowRight className="h-5 w-5 text-orange-600" />
                      <span className="text-sm font-medium text-orange-900">Modifications</span>
                    </div>
                    <p className="text-3xl font-bold text-orange-600">
                      {comparisonResult.modifications?.length || 0}
                    </p>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === "side-by-side" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("side-by-side")}
                  >
                    Vue Côte à Côte
                  </Button>
                  <Button
                    variant={viewMode === "unified" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("unified")}
                  >
                    Vue Unifiée
                  </Button>
                  <Button
                    variant={viewMode === "changes-only" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("changes-only")}
                  >
                    Modifications Uniquement
                  </Button>
                </div>

                {/* Critical Changes */}
                {comparisonResult.critical_changes && comparisonResult.critical_changes.length > 0 && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                      <h3 className="text-lg font-bold text-red-900">
                        Modifications Juridiques Critiques
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {comparisonResult.critical_changes.map((change, idx) => (
                        <div key={idx} className="bg-white border border-red-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="destructive" className="text-xs">
                              {change.severity}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {change.type}
                            </Badge>
                          </div>
                          <p className="font-semibold text-slate-900 mb-2">{change.description}</p>
                          <p className="text-sm text-slate-600">
                            <strong>Impact :</strong> {change.impact}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Detailed Changes */}
                <div className="border border-slate-200 rounded-lg p-6 bg-white max-h-[600px] overflow-y-auto">
                  <h3 className="font-bold text-slate-900 mb-4">Détail des Modifications</h3>
                  
                  {viewMode === "side-by-side" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Version 1
                        </h4>
                        <div className="text-sm space-y-2 text-slate-700">
                          {comparisonResult.text_preview_1}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-orange-900 mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Version 2
                        </h4>
                        <div className="text-sm space-y-2 text-slate-700">
                          {comparisonResult.text_preview_2}
                        </div>
                      </div>
                    </div>
                  )}

                  {viewMode === "changes-only" && (
                    <div className="space-y-3">
                      {comparisonResult.additions?.map((addition, idx) => (
                        <div key={`add-${idx}`} className="bg-green-50 border-l-4 border-green-600 p-3 rounded">
                          <div className="flex items-start gap-2">
                            <PlusCircle className="h-4 w-4 text-green-600 mt-0.5" />
                            <p className="text-sm text-slate-700">{addition}</p>
                          </div>
                        </div>
                      ))}
                      
                      {comparisonResult.deletions?.map((deletion, idx) => (
                        <div key={`del-${idx}`} className="bg-red-50 border-l-4 border-red-600 p-3 rounded">
                          <div className="flex items-start gap-2">
                            <MinusCircle className="h-4 w-4 text-red-600 mt-0.5" />
                            <p className="text-sm text-slate-700 line-through">{deletion}</p>
                          </div>
                        </div>
                      ))}

                      {comparisonResult.modifications?.map((modification, idx) => (
                        <div key={`mod-${idx}`} className="bg-orange-50 border-l-4 border-orange-600 p-3 rounded">
                          <div className="flex items-start gap-2">
                            <ArrowRight className="h-4 w-4 text-orange-600 mt-0.5" />
                            <div className="text-sm">
                              <p className="text-slate-700 line-through mb-1">{modification.before}</p>
                              <p className="text-slate-900 font-medium">{modification.after}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
